/**
 * @file src/components/ReCaptcha.jsx
 * @description Google reCAPTCHA v2 "I'm not a robot" checkbox.
 *
 * Deliberately dependency-free (no `react-google-recaptcha`): the widget is a
 * ~40-line script-loader plus one `grecaptcha.render` call, and an unpinned
 * third-party wrapper on the signup path is more supply-chain surface than the
 * convenience is worth.
 *
 * The SITE key is public by design (it ships in the bundle and is visible in
 * the DOM). Security comes from the backend verifying the returned token
 * against Google with the SECRET key — see App\Services\RecaptchaService.
 *
 * Per-environment key: REACT_APP_RECAPTCHA_SITE_KEY, set in `.env` (dev) and
 * `.env.production` (prod). Swapping environments is a one-line env change and
 * a rebuild — no code edit.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

const SCRIPT_ID = 'google-recaptcha-v2';
const SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';

let scriptPromise = null;

/** Load api.js once per page, no matter how many widgets mount. */
function loadRecaptchaScript() {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    if (window.grecaptcha && window.grecaptcha.render) {
      resolve(window.grecaptcha);
      return;
    }

    let script = document.getElementById(SCRIPT_ID);
    if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    // api.js sets window.grecaptcha before it finishes wiring `render`, so poll
    // for the method rather than trusting the load event alone.
    const started = Date.now();
    const tick = () => {
      if (window.grecaptcha && window.grecaptcha.render) {
        resolve(window.grecaptcha);
      } else if (Date.now() - started > 20000) {
        scriptPromise = null; // let a later mount retry
        reject(new Error('reCAPTCHA script failed to load'));
      } else {
        setTimeout(tick, 100);
      }
    };
    tick();

    script.addEventListener('error', () => {
      scriptPromise = null;
      reject(new Error('reCAPTCHA script failed to load'));
    });
  });

  return scriptPromise;
}

/**
 * @param {(token: string|null) => void} onChange  token on solve, null on expiry/error
 * @param {object} resetSignal  change this value to force a widget reset
 * @param {string} [hl]  UI language code passed to Google
 */
const ReCaptcha = ({ onChange, resetSignal, hl }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const [error, setError] = useState(null);

  // Keep the latest callback without re-rendering the widget (grecaptcha.render
  // is one-shot per container — re-running it throws "already rendered").
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  useEffect(() => {
    let cancelled = false;

    if (!siteKey) {
      setError('missing_site_key');
      return undefined;
    }

    loadRecaptchaScript()
      .then((grecaptcha) => {
        if (cancelled || !containerRef.current || widgetIdRef.current !== null) return;

        widgetIdRef.current = grecaptcha.render(containerRef.current, {
          sitekey: siteKey,
          hl: hl || undefined,
          callback: (token) => onChangeRef.current && onChangeRef.current(token),
          'expired-callback': () => onChangeRef.current && onChangeRef.current(null),
          'error-callback': () => onChangeRef.current && onChangeRef.current(null),
        });
      })
      .catch(() => {
        if (!cancelled) setError('load_failed');
      });

    return () => {
      cancelled = true;
    };
  }, [siteKey, hl]);

  // Reset after a failed submit so the user gets a fresh challenge — a solved
  // token is single-use, and Google rejects a replay with `timeout-or-duplicate`.
  useEffect(() => {
    if (resetSignal === undefined || widgetIdRef.current === null) return;
    if (window.grecaptcha && window.grecaptcha.reset) {
      window.grecaptcha.reset(widgetIdRef.current);
      if (onChangeRef.current) onChangeRef.current(null);
    }
  }, [resetSignal]);

  const reload = useCallback(() => {
    scriptPromise = null;
    setError(null);
    widgetIdRef.current = null;
    loadRecaptchaScript().catch(() => setError('load_failed'));
  }, []);

  if (error === 'missing_site_key') {
    return (
      <small className="p-error d-block">
        Captcha is not configured (REACT_APP_RECAPTCHA_SITE_KEY missing).
      </small>
    );
  }

  return (
    <>
      <div ref={containerRef} />
      {error === 'load_failed' && (
        <small className="p-error d-block mt-1">
          Could not load the captcha.{' '}
          <button type="button" className="btn btn-link p-0 align-baseline" onClick={reload}>
            Retry
          </button>
        </small>
      )}
    </>
  );
};

export default ReCaptcha;
